import { BRAND } from "constants/brand";
/**
 * AddSlider form — modal for creating a new homepage slider.
 *
 * The form enforces the vendor-XOR-link constraint in the UI: selecting a vendor
 * disables the link field and vice-versa. This mirrors the backend validation.
 *
 * New fields:
 * - **المدينة (City)**: Optional geo-targeting. "جميع المدن" means the slider
 *   appears globally. Selecting a specific city restricts it to mobile users
 *   browsing that city.
 * - **إخفاء السلايدر (isHidden)**: When checked, the slider is created in a hidden
 *   state — useful for pre-staging banners before a campaign goes live.
 */
import { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";

import { Form } from "react-bootstrap";

import { CSSTransition } from "react-transition-group";
import { Ring } from "@uiball/loaders";
import useSliders from "hooks/useSliders";
import { fieldOf } from "utils/formField";

const AddSlider = (props) => {
    const form = useRef();
    const nodeRef = useRef();
    const { loading, addSlider, error } = useSliders();
    const [link, setLink] = useState("");
    const [vendorId, setVendorId] = useState("");
    const [isHidden, setIsHidden] = useState(false);
    const [cityId, setCityId] = useState("");

    const submitHandler = async (e) => {
        e.preventDefault();
        const sliderData = new FormData();

        if (fieldOf(form.current, "image").files[0]) {
            sliderData.append(
                "image",
                fieldOf(form.current, "image").files[0],
                fieldOf(form.current, "image").files[0].name,
            );
        }
        sliderData.append("title", fieldOf(form.current, "title").value);
        if (vendorId) {
            sliderData.append("vendorId", vendorId);
        }
        if (link) {
            sliderData.append("link", link);
        }
        sliderData.append("isHidden", isHidden);
        if (cityId) {
            sliderData.append("cityId", cityId);
        }

        try {
            const response = await addSlider(sliderData);

            if (response.slider) {
                props.showMessage("success", "تمت الاضافه", "تمت إضافه سلايدر بنجاح");
                setLink("");
                setVendorId("");
                setIsHidden(false);
                setCityId("");
                props.close();
            } else {
                props.showMessage(
                    "error",
                    "هناك خطأ",
                    response.response.data.message || "حدث خطأ غير متوقع",
                );
            }
        } catch (err) {
            console.error("Error adding slider:", err);
            props.showMessage("error", "هناك خطأ", error || "حدث خطأ غير متوقع");
        }
    };

    return (
        <>
            <div className={`model ${props.show ? "model-show" : ""}`} onClick={props.close}>
                <CSSTransition
                    mountOnEnter
                    unmountOnExit
                    nodeRef={nodeRef}
                    in={props.show}
                    timeout={400}
                    classNames="show"
                >
                    <div className="addItem" ref={nodeRef} onClick={(e) => e.stopPropagation()}>
                        {loading ? (
                            <div className="loading-spinner center">
                                <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                            </div>
                        ) : null}
                        <div className="head center">
                            <h5>إضافه سلايدر</h5>
                            <FontAwesomeIcon icon={faClose} onClick={props.close} />
                        </div>
                        <Form className="form" ref={form} onSubmit={submitHandler}>
                            <Form.Group className="mb-3" controlId="image">
                                <Form.Label>
                                    صوره السلايدر<span>*</span>
                                </Form.Label>
                                <Form.Control
                                    name="image"
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.svg,.webp"
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="name">
                                <Form.Label>
                                    اسم السلايدر<span>*</span>
                                </Form.Label>
                                <Form.Control
                                    name="title"
                                    type="text"
                                    placeholder="اسم السلايدر"
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="vendor">
                                <Form.Label>المطعم</Form.Label>
                                <Form.Select
                                    value={vendorId}
                                    disabled={!!link}
                                    onChange={(e) => {
                                        setVendorId(e.target.value);
                                        setLink("");
                                    }}
                                >
                                    <option value={""}>----</option>
                                    {props.vendors?.map((vendor) => (
                                        <option key={vendor.id} value={vendor.id}>
                                            {vendor.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="link">
                                <Form.Label>رابط خارجي</Form.Label>
                                <Form.Control
                                    type="url"
                                    placeholder="https://example.com"
                                    value={link}
                                    disabled={!!vendorId}
                                    onChange={(e) => {
                                        setLink(e.target.value);
                                        setVendorId("");
                                    }}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="city">
                                <Form.Label>المدينة</Form.Label>
                                <Form.Select
                                    value={cityId}
                                    onChange={(e) => setCityId(e.target.value)}
                                >
                                    <option value={""}>جميع المدن</option>
                                    {props.cities?.map((city) => (
                                        <option key={city.id} value={city.id}>
                                            {city.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="isHidden">
                                <Form.Check
                                    type="checkbox"
                                    label="إخفاء السلايدر"
                                    checked={isHidden}
                                    onChange={(e) => setIsHidden(e.target.checked)}
                                />
                            </Form.Group>

                            <div className="buttons">
                                <button className="cancel" onClick={props.close} type="button">
                                    إلغاء
                                </button>
                                <button className="button" type="submit">
                                    إضافه سلايدر
                                </button>
                            </div>
                        </Form>
                    </div>
                </CSSTransition>
            </div>
        </>
    );
};
export default AddSlider;
